import { Stack } from '@koob/margaret';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import { Modal } from 'components';
import { KoobButton } from '@koob/ui';
import { useSnack, useError } from 'hooks';
import {
  ConfirmAdminBookingForm,
  RefuseAdminBookingForm,
  OtherProposalAdminBookingForm,
} from 'components/Forms/HotelKeeper';
import { Text } from 'ui';
import RespondBookingModal from 'components/RespondBookingModal';
import { updateHotelKeeperBookingState } from '../../api/node/hotelkeeper';
import { LinkedBookingHotels } from '@koob/booking';

const FooterBase = styled(Stack).attrs({
  size: 'full',
  alignX: 'center',
  alignY: 'center',
  gutterSize: 1,
})`
  box-shadow: 0 -1px 2px ${({ theme }) => theme.separator};
  padding: ${({ theme }) => theme.spacing(0.5)};
  background-color: #fff;
`;

const Space = styled.div`
  height: 1rem;
`;

export const CanceledModal = ({
  handleSubmit,
  setUpdateState,
  linkedBookingHotels = [],
}) => {
  const { t } = useTranslation('hotelKeeper');
  const theme = useTheme();

  return (
    <RespondBookingModal
      title={t('refuseBooking')}
      isOpen
      setIsOpen={() => setUpdateState(null)}
    >
      <Text fontWeight={700} type="h3" color="text">
        {t('misc:warning')}
      </Text>
      <Space />
      <div style={{ color: theme.danger }}>
        <Trans i18nKey="refusedModal">
          You are about to <strong>refuse this booking</strong>, are you sure
          you want to continue?
        </Trans>
      </div>

      {linkedBookingHotels.length > 0 && (
        <LinkedBookingHotels
          hotels={linkedBookingHotels}
          title={t('associatedHotelsNotAutoRefused', {
            count: linkedBookingHotels.length,
          })}
        />
      )}
      <RefuseAdminBookingForm
        onSubmit={handleSubmit}
        onClose={() => setUpdateState(null)}
      />
    </RespondBookingModal>
  );
};

const OtherProposalModal = ({ handleSubmit, setUpdateState, booking }) => {
  const { t } = useTranslation('hotelKeeper');

  return (
    <Modal
      size="medium"
      title={t('otherProposal')}
      isOpen
      onRequestClose={() => setUpdateState(null)}
    >
      <Stack direction="column">
        <Text color="textLighter">{t('otherProposalInstruction1')}</Text>
        <Text color="textLighter">{t('otherProposalInstruction2')}</Text>
      </Stack>
      <OtherProposalAdminBookingForm
        onSubmit={handleSubmit}
        onClose={() => setUpdateState(null)}
        booking={booking}
      />
    </Modal>
  );
};

export const ConfirmedModal = ({ handleSubmit, setUpdateState }) => {
  const { t } = useTranslation('hotelKeeper');
  const theme = useTheme();

  return (
    <RespondBookingModal
      title={t('confirmBooking')}
      isOpen
      setIsOpen={() => setUpdateState(null)}
    >
      <div style={{ color: theme.success }}>
        <Trans i18nKey="confirmModal">
          You are about to <strong>confirm this booking</strong>, are you sure
          you want to continue?
        </Trans>
      </div>

      <ConfirmAdminBookingForm
        onSubmit={handleSubmit}
        onClose={() => setUpdateState(null)}
      />
    </RespondBookingModal>
  );
};

const RespondBooking = ({ refetch, booking }) => {
  const { t } = useTranslation('hotelKeeper');
  const [updateState, setUpdateState] = useState(null);
  const { notify } = useSnack();
  const { sendErrorSnack } = useError();
  const { secret } = useParams();

  const handleSubmit = async (values) => {
    try {
      await updateHotelKeeperBookingState({
        secret: secret,
        body: {
          state: updateState,
          ...values,
        },
      });
      notify(t(`notify.${updateState}`));
      refetch();
    } catch (err) {
      sendErrorSnack(err);
    }
  };

  return (
    <>
      {updateState === 'refuse' && (
        <CanceledModal
          setUpdateState={setUpdateState}
          updateState={updateState}
          handleSubmit={handleSubmit}
        />
      )}

      {updateState === 'confirm' && (
        <ConfirmedModal
          setUpdateState={setUpdateState}
          updateState={updateState}
          handleSubmit={handleSubmit}
        />
      )}

      {updateState === 'other_proposal' && (
        <OtherProposalModal
          setUpdateState={setUpdateState}
          updateState={updateState}
          handleSubmit={handleSubmit}
          booking={booking}
        />
      )}

      <FooterBase>
        <KoobButton
          type="button"
          variant="danger"
          onClick={() => setUpdateState('refuse')}
        >
          {t('refuse')}
        </KoobButton>
        <KoobButton
          type="button"
          variant="secondary"
          onClick={() => setUpdateState('other_proposal')}
        >
          {t('otherProposal')}
        </KoobButton>
        <KoobButton
          type="button"
          variant="primary"
          onClick={() => setUpdateState('confirm')}
        >
          {t('confirm')}
        </KoobButton>
      </FooterBase>
    </>
  );
};

export default RespondBooking;
